import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { readGuest, writeGuest } from "@/lib/guest";
import { unlockAudio } from "@/lib/audioUnlock";
import { toast } from "sonner";
import { Check, ArrowRight, ArrowLeft } from "lucide-react";

const STEPS = ["name", "gender", "mode", "character", "atmosphere", "duration"];

// v1.0 onboarding presents ONLY what the Free plan can immediately deliver.
// Mirrors backend FREE_LIMITS — the full catalog is discovered inside the app.
const FREE_COMPANIONS = ["elizabeth", "seriny"];
const FREE_ATMOSPHERES = ["cozy_cabin", "seaside_evening"];
const FREE_MAX_DURATION = 15;

export default function Meet() {
  const nav = useNavigate();
  const guest = readGuest();

  const [step, setStep] = useState(0);
  const [name, setName] = useState(guest.user_name || "");
  const [userGender, setUserGender] = useState(guest.user_gender || null);
  const mode = "sleep"; // v1.0 — Sleep is the only shipped mode
  const [characterId, setCharacterId] = useState(
    FREE_COMPANIONS.includes(guest.character_id) ? guest.character_id : null
  );
  const [atmoChoice, setAtmoChoice] = useState(
    FREE_ATMOSPHERES.includes(guest.atmosphere_id) ? guest.atmosphere_id : null
  );
  const [duration, setDuration] = useState(
    Math.min(guest.duration_minutes || FREE_MAX_DURATION, FREE_MAX_DURATION)
  );

  const [characters, setCharacters] = useState([]);
  const [atmospheres, setAtmospheres] = useState([]);

  // Swipe-peek state — the five pages live side-by-side in one horizontal space.
  const viewportRef = useRef(null);
  const panelRefs = useRef([]);
  const drag = useRef({ active: false, decided: false, horizontal: false, startX: 0, startY: 0, width: 1 });
  const [dragX, setDragX] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [viewportH, setViewportH] = useState(null);

  useEffect(() => {
    Promise.all([api.get("/catalog/characters"), api.get("/catalog/atmospheres")])
      .then(([c, a]) => {
        setCharacters((c.data || []).filter((x) => FREE_COMPANIONS.includes(x.id)));
        setAtmospheres((a.data || []).filter((x) => FREE_ATMOSPHERES.includes(x.id)));
      })
      .catch(() => toast.error("Could not load companions."));
  }, []);

  const next = () => setStep((s) => Math.min(s + 1, STEPS.length - 1));
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const canNext = () => {
    if (step === 0) return name.trim().length > 0;
    if (step === 1) return !!userGender;
    if (step === 2) return !!mode;
    if (step === 3) return !!characterId;
    if (step === 4) return !!atmoChoice;
    if (step === 5) return duration >= 5 && duration <= FREE_MAX_DURATION;
    return true;
  };

  // Start → deposit selections and step into the world immediately.
  // Generation + ambience happen on the Session screen (no loading button here).
  const start = () => {
    unlockAudio(); // real user gesture — arm audio playback for the session screen
    const selectedAtmo = atmospheres.find((a) => a.id === atmoChoice) || { id: atmoChoice };
    writeGuest({
      user_name: name.trim(),
      user_gender: userGender,
      character_id: characterId,
      mode,
      duration_minutes: duration,
      atmosphere_id: atmoChoice,
      atmosphere: selectedAtmo,
      custom_atmosphere: null,
      // Clear any prior evening so the Session screen regenerates a fresh one.
      narration: null,
      summary: null,
    });
    nav("/meet/session", { replace: true });
  };

  const canGoNext = () => step < STEPS.length - 1 && canNext();
  const canGoPrev = () => step > 0;

  // Keep the viewport height matched to the active page so short pages don't
  // inherit the tall companion page's height.
  // Iter 89 — CRITICAL onboarding layout fix: a one-shot measurement could
  // under-report the panel height on small iPhones (fonts still loading,
  // text wrapping to more lines), which pushed the Back/Continue row UP over
  // the name input and made it untappable. A ResizeObserver keeps the
  // viewport height continuously in sync with the active panel's real size.
  useLayoutEffect(() => {
    const el = panelRefs.current[step];
    if (!el) return undefined;
    setViewportH(el.offsetHeight);
    const ro = new ResizeObserver(() => setViewportH(el.offsetHeight));
    ro.observe(el);
    return () => ro.disconnect();
  }, [step, characters, atmospheres]);

  useEffect(() => {
    const onResize = () => { const el = panelRefs.current[step]; if (el) setViewportH(el.offsetHeight); };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [step]);

  const onPointerDown = (e) => {
    if (e.target.closest('input, button, a, textarea, [role="slider"], [data-no-swipe]')) { drag.current.active = false; return; }
    drag.current = { active: true, decided: false, horizontal: false, startX: e.clientX, startY: e.clientY, width: viewportRef.current?.offsetWidth || window.innerWidth || 1 };
  };

  const onPointerMove = (e) => {
    const d = drag.current;
    if (!d.active) return;
    const dx = e.clientX - d.startX;
    const dy = e.clientY - d.startY;
    if (!d.decided) {
      if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
      d.decided = true;
      d.horizontal = Math.abs(dx) > Math.abs(dy);
      if (d.horizontal) {
        setDragging(true);
        try { e.currentTarget.setPointerCapture(e.pointerId); } catch (err) { /* ignore */ }
      }
    }
    if (!d.horizontal) return;
    let move = dx;
    // Rubber-band when there's no page that way (or forward isn't allowed yet).
    if ((move < 0 && !canGoNext()) || (move > 0 && !canGoPrev())) move = move * 0.28;
    setDragX(move);
  };

  const finishDrag = (e) => {
    const d = drag.current;
    if (!d.active) return;
    d.active = false;
    if (!d.horizontal) { setDragging(false); setDragX(0); return; }
    const dx = (e.clientX || d.startX) - d.startX;
    const threshold = Math.min(120, d.width * 0.22);
    setDragging(false);
    if (dx < -threshold && canGoNext()) setStep((s) => s + 1);
    else if (dx > threshold && canGoPrev()) setStep((s) => s - 1);
    setDragX(0);
  };

  return (
    <div className="min-h-screen relative overflow-hidden text-violet-50">

      <div className="relative z-10 max-w-3xl mx-auto px-6 sm:px-8 pt-safe-top pb-[max(3rem,env(safe-area-inset-bottom))]">
        <Link to="/" className="flex items-center gap-2.5 mb-12" data-testid="meet-back-to-home">
          <img src="/continua-logo-small.png" alt="" aria-hidden="true" className="w-9 h-9 object-contain moon-pulse" />
          <span className="font-serif-display text-xl">Continua</span>
        </Link>

        {/* progress */}
        <div className="flex gap-1.5 mb-12">
          {STEPS.map((s, i) => (
            <div key={s}
              className={`h-[2px] flex-1 rounded transition-soft ${i <= step ? "bg-violet-300/80" : "bg-violet-300/15"}`}
              data-testid={`meet-step-indicator-${i}`}
            />
          ))}
        </div>

        <div
          ref={viewportRef}
          className="onboarding-viewport"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={finishDrag}
          onPointerCancel={finishDrag}
          data-testid="meet-swipe-viewport"
          style={{
            touchAction: "pan-y",
            // Iter 89 — the viewport carries 32px vertical padding (for glow
            // overflow) under border-box sizing, so the measured panel height
            // must be topped up by that padding or panels overflow onto the
            // Back/Continue row (the small-iPhone overlap bug).
            height: viewportH ? `calc(${viewportH}px + 64px)` : undefined,
            transition: "height 380ms cubic-bezier(0.22,0.61,0.36,1)",
          }}
        >
          <div
            className="flex items-start"
            style={{
              transform: `translateX(calc(${-step * 100}% + ${dragX}px))`,
              transition: dragging ? "none" : "transform 380ms cubic-bezier(0.22,0.61,0.36,1)",
            }}
          >
          <div className="w-full shrink-0" ref={(el) => (panelRefs.current[0] = el)} data-testid="meet-panel-name">
            <div>
              <p className="text-xs uppercase tracking-[0.28em] text-violet-300/80 mb-4">First — your name</p>
              <h2 className="font-serif-display text-4xl md:text-5xl mb-3 leading-tight">What should I call you?</h2>
              <p className="text-violet-200/70 mb-10 max-w-lg">She'll remember it, and use it softly when she greets you.</p>
              <input
                type="text" autoFocus value={name}
                onChange={(e) => setName(e.target.value)}
                data-testid="meet-name-input"
                className="w-full max-w-md bg-indigo-950/40 border border-violet-300/20 rounded-xl px-5 py-4 text-violet-50 text-lg focus:outline-none focus:border-violet-300/50 focus:ring-1 focus:ring-violet-300/40 transition-soft"
                placeholder="A name, or a name you'd like to be called"
              />
            </div>
          </div>
          <div className="w-full shrink-0" ref={(el) => (panelRefs.current[1] = el)} data-testid="meet-panel-gender">
            <div>
              <p className="text-xs uppercase tracking-[0.28em] text-violet-300/80 mb-4">And — how you're addressed</p>
              <h2 className="font-serif-display text-4xl md:text-5xl mb-3 leading-tight">How would you like to be addressed?</h2>
              <p className="text-violet-200/70 mb-10 max-w-lg">She'll tailor pronouns and terms of endearment to match.</p>
              <div className="grid sm:grid-cols-3 gap-4 max-w-xl">
                {[
                  { id: "male", label: "Male" },
                  { id: "female", label: "Female" },
                  { id: "neutral", label: "Neutral" },
                ].map((g) => (
                  <button
                    key={g.id}
                    type="button"
                    onClick={() => setUserGender(g.id)}
                    data-testid={`meet-gender-${g.id}`}
                    className={`text-left p-5 rounded-2xl border card-selectable ${userGender === g.id ? "is-selected border-violet-300/60 glass-strong glow-silver" : "border-violet-300/15 glass hover:border-violet-300/30"}`}
                  >
                    <h3 className="font-serif-display text-2xl">{g.label}</h3>
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div className="w-full shrink-0" ref={(el) => (panelRefs.current[2] = el)} data-testid="meet-panel-mode">
            <div>
              <p className="text-xs uppercase tracking-[0.28em] text-violet-300/80 mb-4">How tonight will feel</p>
              <h2 className="font-serif-display text-4xl md:text-5xl mb-10 leading-tight">Tonight, she stays until you sleep.</h2>
              <div className="grid md:grid-cols-2 gap-5">
                <button
                  type="button"
                  data-testid="meet-mode-sleep"
                  className="text-left p-7 rounded-2xl border card-selectable is-selected border-violet-300/60 glass-strong glow-silver"
                >
                  <div className="text-xs uppercase tracking-[0.28em] text-violet-300/80 mb-3">Mode I</div>
                  <h3 className="font-serif-display text-2xl mb-2">Sleep</h3>
                  <p className="text-sm text-violet-200/75 leading-relaxed">Gentle opening, quiet conversation, then a slow drift into living presence. The classic evening — designed to be the last thing you hear.</p>
                </button>
                <div
                  data-testid="meet-mode-coming-soon"
                  aria-disabled="true"
                  className="text-left p-7 rounded-2xl border border-violet-300/10 glass opacity-60 cursor-default select-none"
                >
                  <div className="text-xs uppercase tracking-[0.28em] text-indigo-200/70 mb-3">Coming Soon</div>
                  <h3 className="font-serif-display text-2xl mb-2">More ways to be together</h3>
                  <p className="text-sm text-violet-200/65 leading-relaxed">New modes are on their way — quieter evenings, slower mornings, and more. Tonight begins with Sleep.</p>
                </div>
              </div>
            </div>
          </div>
          <div className="w-full shrink-0" ref={(el) => (panelRefs.current[3] = el)} data-testid="meet-panel-character">
            <div>
              <p className="text-xs uppercase tracking-[0.28em] text-violet-300/80 mb-4">Who's waiting</p>
              <h2 className="font-serif-display text-4xl md:text-5xl mb-10 leading-tight">Choose your companion.</h2>
              <div className="grid md:grid-cols-2 gap-5 staggered">
                {characters.map((c) => (
                  <button
                    key={c.id} type="button" onClick={() => setCharacterId(c.id)}
                    data-testid={`meet-character-${c.id}`}
                    className={`group text-left rounded-2xl overflow-hidden border card-selectable ${characterId === c.id ? "is-selected border-violet-300/60 glow-violet" : "border-violet-300/15 hover:border-violet-300/35"}`}
                  >
                    <div className="aspect-[3/4] relative overflow-hidden">
                      <img src={c.image} alt={c.name} className="w-full h-full object-cover transition-soft group-hover:scale-105" />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a1a] via-[#0a0a1a]/30 to-transparent" />
                      {characterId === c.id && (
                        <div className="absolute top-3 right-3 w-7 h-7 rounded-full bg-violet-200 text-indigo-950 flex items-center justify-center">
                          <Check className="w-4 h-4" />
                        </div>
                      )}
                      <div className="absolute bottom-0 left-0 right-0 p-6">
                        <div className="text-xs uppercase tracking-[0.25em] text-violet-300/80 mb-1">{c.archetype}</div>
                        <h3 className="font-serif-display text-3xl">{c.name}</h3>
                        <p className="text-sm text-violet-200/75 mt-2">{c.tagline}</p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div className="w-full shrink-0" ref={(el) => (panelRefs.current[4] = el)} data-testid="meet-panel-atmosphere">
            <div>
              <p className="text-xs uppercase tracking-[0.28em] text-violet-300/80 mb-4">Where you'll meet</p>
              <h2 className="font-serif-display text-4xl md:text-5xl mb-3 leading-tight">Where will you meet tonight?</h2>
              <p className="text-violet-200/70 mb-10 max-w-lg">Choose a place to settle into.</p>

              <div className="grid sm:grid-cols-2 gap-5 staggered">
                {atmospheres.map((a) => (
                  <button
                    key={a.id} type="button" onClick={() => setAtmoChoice(a.id)}
                    data-testid={`meet-atmosphere-${a.id}`}
                    className={`text-left p-7 rounded-2xl border card-selectable ${atmoChoice === a.id ? "is-selected border-violet-300/60 glass-strong glow-silver" : "border-violet-300/15 glass hover:border-violet-300/30"}`}
                  >
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <h3 className="font-serif-display text-2xl">{a.name}</h3>
                      {atmoChoice === a.id && (
                        <div className="w-6 h-6 rounded-full bg-violet-200 text-indigo-950 flex items-center justify-center shrink-0">
                          <Check className="w-3.5 h-3.5" />
                        </div>
                      )}
                    </div>
                    <p className="text-sm text-violet-200/70 leading-relaxed">{a.description}</p>
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div className="w-full shrink-0" ref={(el) => (panelRefs.current[5] = el)} data-testid="meet-panel-duration">
            <div>
              <p className="text-xs uppercase tracking-[0.28em] text-violet-300/80 mb-4">How long tonight</p>
              <h2 className="font-serif-display text-4xl md:text-5xl mb-3 leading-tight">How long should she stay with you?</h2>
              <p className="text-violet-200/70 mb-10 max-w-lg">You can change this every time.</p>

              <div className="glass rounded-2xl p-7 max-w-lg" data-testid="meet-duration-card">
                <div className="flex items-baseline gap-3 mb-5">
                  <span className="font-serif-display text-5xl text-violet-50" data-testid="meet-duration-value">{duration}</span>
                  <span className="text-sm text-violet-300/70">minutes</span>
                </div>
                <input
                  type="range"
                  min={5}
                  max={FREE_MAX_DURATION}
                  step={1}
                  value={duration}
                  onChange={(e) => setDuration(Number(e.target.value))}
                  data-testid="meet-duration-slider"
                  className="continua-slider w-full"
                />
                <div className="flex justify-between text-[10px] uppercase tracking-[0.2em] text-violet-300/50 mt-2">
                  <span>5 min</span>
                  <span>{FREE_MAX_DURATION} min</span>
                </div>
              </div>
            </div>
          </div>
          </div>
        </div>

        {/* Iter 89 — footer nav always sits BELOW the panel content (relative
            z-20 guarantees taps land on the buttons even if a panel briefly
            overflows during height animation on small screens). */}
        <div className="relative z-20 flex items-center justify-between mt-14">
          <button
            type="button" onClick={back} disabled={step === 0}
            data-testid="meet-back-button"
            className="inline-flex items-center gap-2 text-sm text-violet-300/60 hover:text-violet-200 transition-soft disabled:opacity-30"
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </button>

          {step < STEPS.length - 1 ? (
            <button
              type="button" onClick={next} disabled={!canNext()}
              data-testid="meet-next-button"
              className="inline-flex items-center gap-2 rounded-full px-7 py-3 bg-violet-200 text-indigo-950 font-medium hover:bg-violet-100 transition-soft disabled:opacity-40"
            >
              Continue <ArrowRight className="w-4 h-4" />
            </button>
          ) : (
            <button
              type="button" onClick={start} disabled={!canNext()}
              data-testid="meet-start-button"
              className="inline-flex items-center gap-2 rounded-full px-8 py-3 bg-violet-200 text-indigo-950 font-medium hover:bg-violet-100 transition-soft disabled:opacity-50 glow-violet"
            >
              Begin <ArrowRight className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
